import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

import ResultScreen from "../systemUtils/ResultScreen";
import MultiStyleButton from "../systemUtils/MultiStyleButton";

import "../stylesHero/AddForm.css";

const AddETQA = ({
  company = "TPC_Group",
  collectionBrand = "TechPerformanceCentre",
  systemName = "etqaOfficer",
  onAdd,
  onComplete,
  existingEmails = [],
}) => {
  // Form state
  const [form, setForm] = useState({
    name: "",
    surname: "",
    number: "",
    email: "",
    profilePic: "",
  });
  // Profile picture upload state
  const [profilePicDataUrl, setProfilePicDataUrl] = useState("");
  const [profilePicName, setProfilePicName] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [inlineError, setInlineError] = useState("");

  // Handle text input changes
  const handleChange = ({ target }) => {
    setForm((prev) => ({ ...prev, [target.name]: target.value }));
    if (inlineError) setInlineError("");
  };

  // Handle profile picture file selection
  const handleFileChange = ({ target }) => {
    const file = target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setResult({
        type: "error",
        message: "Please select a valid image file.",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setResult({
        type: "error",
        message: "Image size should be less than 5MB.",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setProfilePicDataUrl(e.target.result);
      setProfilePicName(file.name);
    };
    reader.readAsDataURL(file);
  };

  // Add ETQA officer
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setInlineError("");

    try {
      const normalizedEmail = form.email.trim().toLowerCase();
      if (existingEmails.includes(normalizedEmail)) {
        setInlineError("ETQA officer already exists.");
        setLoading(false);
        return;
      }

      let profilePicUrl = form.profilePic || "";

      if (profilePicDataUrl) {
        const base64 = profilePicDataUrl.split(",")[1];
        const ext =
          profilePicName.split(".").pop()?.toLowerCase() || "jpg";
        const fileName = `profile_${Date.now()}.${ext}`;

        const fn = getFunctions();
        const uploadPic = httpsCallable(fn, "uploadProfilePicFacilitator");
        const uploadRes = await uploadPic({
          company,
          collectionBrand,
          system: "ETQA",
          uid: form.email,
          fileName,
          fileBuffer: base64,
        });

        profilePicUrl = uploadRes.data?.url || "";
      }

      const payload = {
        name: form.name,
        surname: form.surname,
        number: form.number,
        email: normalizedEmail,
        profilePic: profilePicUrl,
      };

      const fn = getFunctions();
      const call = httpsCallable(fn, "addFacilitatorRecord");
      const res = await call({
        company,
        collectionBrand,
        system: systemName,
        formData: payload,
      });

      console.log("addETQA result:", res.data);
      setResult({ type: "success", message: "ETQA officer saved!" });
      if (onAdd) onAdd(payload);

      setInlineError("");
      setForm({
        name: "",
        surname: "",
        number: "",
        email: "",
        profilePic: "",
      });
      setProfilePicDataUrl("");
      setProfilePicName("");

      setTimeout(() => onComplete && onComplete(), 1200);
    } catch (err) {
      console.error("addETQA error:", err);
      setResult({
        type: "error",
        message: err.message || "Something went wrong.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Result screen (success/error)
  if (result) {
    return (
      <ResultScreen
        type={result.type}
        message={result.message}
        onContinue={() => {
          if (result.type === "success") {
            if (onComplete) {
              onComplete();
            } else {
              setResult(null);
            }
          } else {
            setResult(null);
          }
        }}
      />
    );
  }

  // Form UI
  return (
    <div className="add-user-container">
      <h2 className="add-user-heading">Add ETQA Officer</h2>
      <form onSubmit={handleSubmit} className="input-form">
        {[
          { label: "Name", name: "name", required: true },
          { label: "Surname", name: "surname", required: true },
          { label: "Number", name: "number", required: true },
          { label: "Email", name: "email", type: "email", required: true },
        ].map(({ label, name, type = "text", required }) => (
          <div className="input-group" key={name}>
            <label className="label">{label}</label>
            <input
              className="text-input"
              name={name}
              type={type}
              value={form[name]}
              required={required}
              onChange={handleChange}
            />
          </div>
        ))}
        <div className="input-group">
          <label className="label">Profile Pic</label>
          <input
            className="text-input"
            name="profilePicFile"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
        </div>

        {inlineError && (
          <div className="etqa-inline-error">{inlineError}</div>
        )}
        <MultiStyleButton
          buttonType="wide"
          label={loading ? "Saving..." : "Submit"}
          disabled={loading}
          onClick={() => {}}
        />
      </form>
    </div>
  );
};

export default AddETQA;
